"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./ModalErrorRenderer");

/**
 * @typedef GlueErrorObject
 * @type {object}
 * @property {string} errorCode - A Name that represents the error that was encountered.
 * @property {string} errorMsg - A message that gives the user a high level understanding of the error.
 * //TODO add optional properties
 */
/**
 * @event ErrorCodeComponent:ErrorUpdated
 * @param {GlueErrorObject} newVal: The new Error object
 * @param {GlueErrorObject} oldVal: The previous Error object
 */
/**
 * @event ErrorCodeComponent:ErrorCodeUpdated
 * @param {string} newVal: The new Error Code
 * @param {string} oldVal: The previous Error Code
 */
/**
 * @event ErrorCodeComponent:ErrorMsgUpdated
 * @param {string} newVal: The new Error Message
 * @param {string} oldVal: The previous Error Message
 */
/**
 * @event ErrorCodeComponent:TitleChanged
 * @param {string} newVal: The new Error Title
 * @param {string} oldVal: The previous Error Title
 */

/**
 * @event ErrorCodeComponent:DarkThemeChanged
 * @param {string} newVal: The new dark theme state
 * @param {string} oldVal: The previous dark theme state
 */
/**
 * @fires ErrorCodeComponent:ErrorUpdated
 * @fires ErrorCodeComponent:ErrorCodeUpdated
 * @fires ErrorCodeComponent:ErrorMsgUpdated
 * @fires ErrorCodeComponent:TitleChanged
 * @fires ErrorCodeComponent:DarkThemeChanged
 */
class ModalErrorComponent extends EventEmitter {
    constructor(opts) {
        super();
        this._renderer = new Renderer(this);
        this._glueError = undefined;
        this._errorMsgFormat = undefined;

        if (_.get(opts, "error")) {
            this.setError(_.get(opts, "error"), true);
        }
        if (_.get(opts, "errorCode") || _.get(opts, "errorCode") === "") {
            this.setErrorCode(_.get(opts, "errorCode"), true);
        }
        if (_.get(opts, "errorMsg")) {
            this.setErrorMsg(_.get(opts, "errorMsg"), true);
        }

        this.setErrorMsgFormat(
            _.get(opts, "errorMsgFormat", /<[a-z][\s\S]*>/i.test(this.errorMsg) ? "html" : "text")
        );

        if (_.get(opts, "errorTitle")) {
            this.setErrorTitle(_.get(opts, "errorTitle"), true);
        }
        this._confirmationationDetails = _.defaults({}, _.get(opts, "confirmDetails"), {
            type: "text",
            text: "OK",
        });
        this.setDarkTheme(_.get(opts, "darkTheme", false), true);
        this._registerListeners();
        this._renderer.render();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();
        return this._renderer.shutdown().then(
            () => {
                this._renderer = undefined;
                this._glueError = undefined;
                this._errorTitle = undefined;
                this._darkTheme = undefined;
            },
            (/*err*/) => {
                this._renderer = undefined;
                this._glueError = undefined;
                this._errorTitle = undefined;
                this._darkTheme = undefined;
            }
        );
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/
    /**
     * Currently set error object
     * @type {GlueErrorObject}
     */
    get error() {
        return this.getError();
    }

    /**
     * Gets the currently set error object
     * @returns {GlueErrorObject}
     */
    getError() {
        return _.get(this, "_glueError", {
            errorCode: this.getErrorCode(),
            errorMsg: this.getErrorMsg(),
        });
    }

    set error(glueError) {
        this.setError(glueError);
    }

    /**
     * Set the glueError object.
     * @param {GlueErrorObject} glueError - The Glue Error object to be set.
     * @param {boolean} [quiet=false] - Do this update quietly do NOT fire any change events.
     * @returns {ModalErrorComponent}
     */
    setError(glueError, quiet) {
        let prevError = this.getError();
        this._glueError = glueError;
        if (!quiet) {
            this.emit("ErrorUpdated", this._glueError, prevError);
        }
        return this;
    }

    /**
     * The Error's code
     * @type {string|undefined}
     */
    get errorCode() {
        return this.getErrorCode();
    }

    /**
     * Get the Current Error's Code.
     * @returns {string|undefined} Undefined if no errorCode is set.
     */
    getErrorCode() {
        return _.get(this, "_glueError.errorCode", "UNKNOWN_ERROR");
    }

    set errorCode(ec) {
        this.setErrorCode(ec);
    }

    /**
     * Sets the current Error's Error Code.
     * @param {string} ec - The Error Code to set.
     * @param {boolean} [quiet=false] - Do this update quietly do NOT fire any change events.
     */
    setErrorCode(ec, quiet) {
        let prevEc = this.getErrorCode();
        _.set(this, "_glueError.errorCode", ec);
        if (!quiet) {
            this.emit("ErrorCodeUpdated", ec, prevEc);
        }
        return this;
    }

    /**
     * The Error's message.
     * @type {string}
     */
    get errorMsg() {
        return this.getErrorMsg();
    }

    /**
     * Get the Current Error's Message.
     * @returns {string|undefined} Undefined if no errorMsg is set.
     */
    getErrorMsg() {
        return _.get(this, "_glueError.errorMsg", "We have encountered an unknown error.");
    }

    set errorMsg(em) {
        this.setErrorMsg(em);
    }

    /**
     * Get the Current Error's Message Format.
     * @returns {string} [quiet="html" if errMsg contains HTML tags, otherwise "text"]
     */
    get errorMsgFormat() {
        return this.getMsgFormat();
    }

    getErrorMsgFormat() {
        return this._errorMsgFormat;
    }

    isHTMLErrorMsg() {
        return this._errorMsgFormat.toLowerCase() === "html";
    }

    /**
     * Sets the current Error's Error Message Format.
     * @param {string} emf - Error Message Format to set (either 'html' or 'text).
     * @param {boolean} [quiet=false] - Do this update quietly do NOT fire any change events.
     */
    setErrorMsgFormat(emf, quiet) {
        let prevEm = this.getErrorMsgFormat();
        _.set(this, "_errorMsgFormat", emf);
        if (!quiet) {
            this.emit("ErrorMsgFormatUpdated", emf, prevEm);
        }
        return this;
    }

    /**
     * Sets the current Error's Error Message.
     * @param {string} em - The Error Message to set.
     * @param {boolean} [quiet=false] - Do this update quietly do NOT fire any change events.
     */
    setErrorMsg(em, quiet) {
        let prevEm = this.getErrorMsg();
        _.set(this, "_glueError.errorMsg", em);
        if (!quiet) {
            this.emit("ErrorMsgUpdated", em, prevEm);
        }
        return this;
    }

    /**
     * The Error's Title
     * @type {string}
     */
    get errorTitle() {
        return this.getTitle();
    }

    /**
     * Get the Error Title
     * @returns {string} The Error Title if specified if it exists, otherwise returns the Error Code.
     */
    getErrorTitle() {
        return _.get(this, "_errorTitle", this.getErrorCode());
    }

    set errorTitle(tit) {
        this.setTitle(tit);
    }

    /**
     * Set the Error Title
     * @param {string} tit - The Error Title to be set.
     * @param {boolean} [quiet=false] - Do this update quietly do NOT fire any change events.
     * @returns {ModalErrorComponent}
     */
    setErrorTitle(tit, quiet) {
        let old = this._errorTitle;
        this._errorTitle = tit;
        if (!quiet) {
            this.emit("TitleChanged", this._errorTitle, old);
        }
        return this;
    }

    /**
     * Returns the dark theme state of the control
     * @returns {boolean} the current dark theme state
     */
    isDarkTheme() {
        return this._darkTheme;
    }

    set darkTheme(newVal) {
        //jshint ignore:line
        this.setDarkTheme(newVal);
    }

    /**
     * Sets the dark theme state and returns an instance of the control for chainability
     * @param {boolean} newVal - The new dark theme state
     * @param {boolean} [quiet=false] - Do this update quietly do NOT fire any change events.
     * @returns {ModalErrorComponent}
     */
    setDarkTheme(newVal, quiet) {
        let old = this._darkTheme;
        this._darkTheme = newVal;
        if (!quiet) {
            this.emit("DarkThemeChanged", this._darkTheme, old);
        }
        return this;
    }

    set "$teve.ui.context"(cntxt) {
        //jshint ignore:line
        this._userFeedbackContext = cntxt;
    }

    get confirmationDeets() {
        return this.getConfirmationDeets();
    }

    getConfirmationDeets() {
        return this._confirmationationDetails;
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /* START PUBLIC ACTION METHODS             */
    /*******************************************/

    confirm() {
        //All Error messages reject on confirmation!
        this._userFeedbackContext.reject();
    }
}

module.exports = ModalErrorComponent;
